import { safeFormatDate } from '@utils/date';

export interface IDraftPreviewField {
    label: string;
    value: string;
}

export interface IDraftPreview {
    fields: IDraftPreviewField[];
    /** Сколько заполненных реквизитов не поместилось в предпросмотр. */
    more: number;
}

interface IRawField {
    name?: unknown;
    value?: unknown;
}

const FIELD_PREFIX = '|Document|';
// Служебные реквизиты формы: есть у любого документа и ничего не говорят о содержимом черновика.
const SKIPPED = new Set(['header', 'Статус', 'Имя_потока', 'ParentRK', 'Дата_регистрации', 'Актуальность', 'Key']);
// Подписи-спутники: |Document|Инициатор — id, |Document|Инициатор_ФИО — текст для человека.
const COMPANION_SUFFIXES = ['_наименование', '_ФИО', '_Наименование'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const VALUE_LIMIT = 140;

const humanize = (key: string): string => key.replace(/_/g, ' ').trim();

// Текст, который пользователь набирал руками (предмет, комментарий), говорит о черновике больше,
// чем подставленные справочником значения, флаги и суммы — такие строки идут первыми.
const isProse = (value: string): boolean =>
    !ISO_DATE.test(value) && !/^[\d\s.,-]+$/.test(value) && (value.includes(' ') || value.length >= 12);

const formatValue = (value: unknown): string | null => {
    if (value === null || value === undefined || value === false) return null;
    if (Array.isArray(value)) {
        return value.length
            ? `${value.length} ${value.length === 1 ? 'строка' : value.length < 5 ? 'строки' : 'строк'}`
            : null;
    }
    if (typeof value === 'object') return null;
    if (typeof value === 'boolean') return 'Да';
    const text = String(value).trim();
    // Пусто и «0» — незаполненный флаг или сумма, в предпросмотре это шум.
    if (!text || text === '0') return null;
    if (ISO_DATE.test(text)) return safeFormatDate(text, 'DD.MM.YYYY') || text;
    return text.length > VALUE_LIMIT ? `${text.slice(0, VALUE_LIMIT - 1).trimEnd()}…` : text;
};

/**
 * Лаконичное содержимое черновика для предпросмотра: только заполненные реквизиты документа
 * в порядке формы, подписи из имени поля, вместо id — текст поля-спутника («_ФИО», «_наименование»),
 * таблицы — числом строк, даты — коротко. Схема формы не нужна, поэтому работает и в лаунчере.
 */
export const summarizeDraftData = (data: string | null | undefined, limit = 8): IDraftPreview => {
    let parsed: unknown;
    try {
        parsed = data ? JSON.parse(data) : null;
    } catch {
        return { fields: [], more: 0 };
    }
    const raw = (parsed as { fields?: unknown } | null)?.fields;
    if (!Array.isArray(raw)) return { fields: [], more: 0 };

    const byKey = new Map<string, unknown>();
    const order: string[] = [];
    (raw as IRawField[]).forEach((field) => {
        if (typeof field?.name !== 'string' || !field.name.startsWith(FIELD_PREFIX)) return;
        const key = field.name.slice(FIELD_PREFIX.length);
        if (!key || byKey.has(key)) return;
        byKey.set(key, field.value);
        order.push(key);
    });

    const companionOf = (key: string): string | undefined =>
        COMPANION_SUFFIXES.map((suffix) => key + suffix).find((candidate) => byKey.has(candidate));
    const isCompanion = (key: string): boolean =>
        COMPANION_SUFFIXES.some((suffix) => key.endsWith(suffix) && byKey.has(key.slice(0, -suffix.length)));

    const prose: IDraftPreviewField[] = [];
    const rest: IDraftPreviewField[] = [];
    order.forEach((key) => {
        if (SKIPPED.has(key) || isCompanion(key)) return;
        const source = companionOf(key) ?? key;
        const raw = byKey.get(source);
        const value = formatValue(raw);
        if (value === null) return;
        // Значение спутника — из справочника, таблица — счётчик, а не набранный текст: в общую очередь.
        (source === key && typeof raw === 'string' && isProse(value) ? prose : rest).push({
            label: humanize(key),
            value,
        });
    });

    const fields = [...prose, ...rest];
    return { fields: fields.slice(0, limit), more: Math.max(0, fields.length - limit) };
};
